import React, { useEffect, useState, useContext } from "react";
import {
  Card,
  DataTable,
  SkeletonBodyText,
  Layout,
  Page,
  Badge,
  Button,
  Link,
  TextStyle,
  DisplayText,
} from "@shopify/polaris";
import { ShopContext } from "../../App";
import currencySymbol from "currency-symbol-map";
import _ from "lodash";
import { DateTime } from "luxon";
import axios from "../../axios";
import { useHistory } from "react-router-dom";
import Br from "../../components/Br";
import { loadAutomations } from "../../utils";

function OrdersPage() {
  const config = useContext(ShopContext);
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState({});
  const [page, setPage] = useState(1);
  const [checkouts, setCheckouts] = useState([]);
  const [pageDetails, setPageDetails] = useState({
    totalPages: 0,
    previousPage: false,
    nextPage: false,
    totalItems: 0,
  });
  const [automations, setAutomations] = useState([]);
  const [shop, setShop] = useState({});

  // Stats
  let deliveredPostOrderPaymentMessages = _.get(
    dashboard,
    "delivered_post_order_payment_messages",
    0
  );

  let deliveredPostOrderDeliveryMessages = _.get(
    dashboard,
    "delivered_post_order_delivery_messages",
    0
  );

  let openedPostOrderPaymentMessages = _.get(
    dashboard,
    "opened_post_order_payment_messages",
    0
  );
  let openedPostOrderDeliveryMessages = _.get(
    dashboard,
    "opened_post_order_delivery_messages",
    0
  );

  let postOrderPaymentSentMessages = _.get(
    dashboard,
    "post_order_payment_sent_messages",
    0
  );
  let postOrderDeliverySentMessages = _.get(
    dashboard,
    "post_order_delivery_sent_messages",
    0
  );

  // stats for Order Delivery & Order Confirmation
  let totalOrderSentMessages =
    Number(postOrderPaymentSentMessages) +
    Number(postOrderDeliverySentMessages);

  let totalOrderDeliveryMessages =
    Number(deliveredPostOrderPaymentMessages) +
    Number(deliveredPostOrderDeliveryMessages);

  let totalOrderOpenedMessages =
    Number(openedPostOrderPaymentMessages) +
    Number(openedPostOrderDeliveryMessages);

  // Percentage stats
  let ordersDeliveryPercentage =
    totalOrderSentMessages > 0
      ? (totalOrderDeliveryMessages * 100) / totalOrderSentMessages
      : 0;

  let ordersOpenedPercentage =
    totalOrderDeliveryMessages > 0
      ? (totalOrderOpenedMessages * 100) / totalOrderDeliveryMessages
      : 0;

  const router = useHistory();
  /**
   * get the skeleton content for page data table
   * @returns {[]}
   */
  const getTableSkeletonContent = () => {
    let skeleton = [];
    for (let i = 0; i < 4; i++) {
      skeleton.push([
        <SkeletonBodyText lines={1} />,
        <SkeletonBodyText lines={1} />,
        <SkeletonBodyText lines={1} />,
        <SkeletonBodyText lines={1} />,
        <SkeletonBodyText lines={1} />,
        <SkeletonBodyText lines={1} />,
      ]);
    }
    return skeleton;
  };
  let skeleton = getTableSkeletonContent();
  /**
   * Column content types of data table
   * @returns {string[]}
   */
  const columnContentTypes = () => {
    return ["text", "text", "text", "text", "text", "text", "text"];
  };
  /**
   * Data table headings
   * @returns {string[]}
   */
  const tableHeadings = () => {
    return [
      "Order",
      "Customer",
      "Amount",
      "Status",
      "Automation",
      "Manual message",
    ];
  };

  /**
   * process the response to generate the table columns data
   * @param orders
   * @param shop
   * @returns {[]}
   */
  const _processResponseOrders = (orders, shop) => {
    let processedOrders = [];
    if (orders.length > 0) {
      orders.forEach((order) => {
        let newOrder = [];
        newOrder.push(
          <>
            <div>
              <Link
                url={`https://${config.shopOrigin}/admin/orders/${order.order_id}`}
                external={true}
              >
                #{order.order_number ? order.order_number : order.order_id}
              </Link>
            </div>
            <Br height={10} />
            <div>
              {DateTime.fromMillis(parseInt(order.completed_at)).toLocaleString(
                DateTime.DATETIME_SHORT
              )}
            </div>
          </>
        );
        newOrder.push(
          <>
            {order.phone_number}
            <Br />
            {order.firstname} {order.lastname}
            <Br />
            Opted-in :&nbsp;
            {order.buyer_accepts_marketing === 1 ? (
              <Badge status="success"> Yes </Badge>
            ) : (
              <Badge status="critical"> No </Badge>
            )}
          </>
        );
        newOrder.push(
          <>
            {currencySymbol(order.presentment_currency)}
            {parseFloat(order.total_amount).toFixed(2)}
          </>
        );
        let deliveryData = null;
        if (shop.has_active_subscription === 1) {
          let paymentMessageSent = _.get(order, "payment_message_sent", null);
          let deliveryMessageSent = _.get(order, "delivery_message_sent", null);
          let paymentStatus = _.get(order, "financial_status", null);
          let fulfillmentStatus = _.get(order, "fulfilment_status", null);
          if (!fulfillmentStatus) {
            fulfillmentStatus = "Unfulfilled";
          }
          newOrder.push(
            <>
              {paymentStatus && (
                <Badge
                  progress={`${
                    paymentStatus === "paid" ? "complete" : "incomplete"
                  }`}
                >
                  {paymentStatus}
                </Badge>
              )}{" "}
              {fulfillmentStatus && (
                <Badge
                  status={`${
                    fulfillmentStatus === "fulfilled" ? "default" : "attention"
                  }`}
                  progress={`${
                    fulfillmentStatus === "fulfilled"
                      ? "complete"
                      : "incomplete"
                  }`}
                >
                  {fulfillmentStatus}
                </Badge>
              )}
            </>
          );
          newOrder.push(
            <div>
              <div>
                Order confirmation notification{" "}
                {paymentMessageSent ? (
                  <Badge status="success">sent</Badge>
                ) : (
                  <Badge status="warning">Not sent</Badge>
                )}
              </div>
              <Br height={10} />
              <div>
                Delivery notification{" "}
                {deliveryMessageSent ? (
                  <Badge status="success">sent</Badge>
                ) : (
                  <Badge status="warning">Not sent</Badge>
                )}
              </div>
            </div>
          );
        } else {
          deliveryData = (
            <Button
              secondary
              onClick={() => {
                router.push(`/pricing`);
              }}
            >
              Upgrade
            </Button>
          );
          newOrder.push(deliveryData);
          newOrder.push(deliveryData);
        }
        /*if (order.recovered_at === null) {*/
        newOrder.push(
          <Button
            primary
            onClick={() => {
              router.push(`/orders/send-message/${order.id}/post_order`);
            }}
          >
            Send message
          </Button>
        );
        /*} else {
                    newOrder.push(<Button disabled>Recovered</Button>);
                }*/
        processedOrders.push(newOrder);
      });
    }
    return processedOrders;
  };

  /**
   * Load the checkouts from API
   * @type {function(): void}
   */
  const loadOrders = () => {
    setLoading(true);
    axios
      .get(`/orders?page=${page}`, {
        headers: {
          shopOrigin: config.shopOrigin,
          xShopHost: config.host,
        },
      })
      .then((response) => {
        let checkouts = _processResponseOrders(
          response.data.data,
          response.shop
        );
        let dashboardResponse = _.get(response.data, "dashboard", {});
        setDashboard(dashboardResponse);
        setCheckouts(checkouts);
        setPageDetails({
          totalPages: response.data.totalPages,
          previousPage: response.data.previousPage,
          nextPage: response.data.nextPage,
          totalItems: response.data.totalItems,
        });
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  /**
   * Load checkouts when page loads
   * or load when the particular state changed
   */
  useEffect(() => {
    loadAutomations(
      setShop,
      setLoading,
      setDashboard,
      setAutomations,
      config.shopOrigin,
      config.host
    );
    loadOrders();
    // eslint-disable-next-line
  }, [page, config]);

  return (
    <>
      <Page
        fullWidth
        title="Orders CRM"
        primaryAction={{
          content: "Refresh",
          accessibilityLabel: "Refresh",
          onAction: () => {
            loadOrders();
          },
          loading: loading,
        }}
        pagination={{
          hasPrevious: pageDetails.previousPage,
          hasNext: pageDetails.nextPage,
          onNext: () => {
            setPage(page + 1);
          },
          onPrevious: () => {
            setPage(page - 1);
          },
        }}
      >
        <div
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          <div style={{ width: "50%", padding: "20px 20px 20px 0px" }}>
            <Card title="" sectioned>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-around",
                  minHeight: "265px",
                }}
              >
                <div style={{ width: "50%" }}>
                  <Br height={25} />
                  <span>
                    <img
                      style={{
                        display: "block",
                        marginLeft: "auto",
                        marginRight: "auto",
                      }}
                      alt=""
                      width="50%"
                      src="../../Assets/illustrations/delivery-rate.svg"
                    />
                  </span>
                  <Br height={25} />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-around",
                    }}
                  >
                    <div>
                      <TextStyle variation="subdued">Delivery Rate</TextStyle>
                      <br />
                      <TextStyle variation="small">
                        {loading && (
                          <>
                            <Br height={5} />
                            <SkeletonBodyText lines={1} />
                          </>
                        )}
                        {!loading && (
                          <span style={{ color: "#008060" }}>
                            {totalOrderDeliveryMessages} deliveries
                          </span>
                        )}
                      </TextStyle>
                    </div>
                    <div>
                      <DisplayText size="medium">
                        {loading && <SkeletonBodyText lines={1} />}
                        {!loading && (
                          <>{Math.ceil(ordersDeliveryPercentage)}%</>
                        )}
                      </DisplayText>
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    borderRight: "1px solid rgba(124, 116, 116, 0.2)",
                  }}
                ></div>
                <div style={{ width: "50%" }}>
                  <span style={{ float: "right" }}>
                    {" "}
                    <Link
                      removeUnderline
                      onClick={() => {
                        router.push(
                          "/message-history?type=post_order_payment,post_order_delivery"
                        );
                      }}
                    >
                      <span style={{ color: "#008060" }}>
                        View message history
                      </span>
                    </Link>
                  </span>
                  <Br height={25} />
                  <img
                    style={{
                      display: "block",
                      marginLeft: "auto",
                      marginRight: "auto",
                    }}
                    alt=""
                    width="50%"
                    src="../../Assets/illustrations/read-rate.svg"
                  />
                  <Br height={25} />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-around",
                    }}
                  >
                    <span>
                      <TextStyle variation="subdued">Read Rate</TextStyle>
                      <br />
                      <TextStyle variation="small">
                        {loading && (
                          <>
                            <Br height={5} />
                            <SkeletonBodyText lines={1} />
                          </>
                        )}
                        {!loading && (
                          <span style={{ color: "#008060" }}>
                            {totalOrderOpenedMessages} opens
                          </span>
                        )}
                      </TextStyle>
                    </span>
                    <span>
                      <DisplayText size="medium">
                        {loading && <SkeletonBodyText lines={1} />}
                        {!loading && <>{Math.ceil(ordersOpenedPercentage)}%</>}
                      </DisplayText>
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
          <div style={{ width: "50%", padding: "0px 20px 20px 0px" }}>
            <div
              style={{
                gridTemplateColumns: "auto auto",
                display: "grid",
                gridGap: "30px;",
              }}
            >
              <div style={{ padding: "20px 0px 0px 0px" }} class="grid-item">
                <Card title="" sectioned>
                  <DisplayText size="medium">
                    {loading && <SkeletonBodyText lines={2} />}
                    {!loading && <>{Number(postOrderDeliverySentMessages)}</>}
                  </DisplayText>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <div>
                      <Br height={20} />
                      <TextStyle variation="subdued">Order</TextStyle>
                      <br />
                      <TextStyle variation="subdued">Delivery</TextStyle>
                    </div>

                    <div style={{ textAlign: "right" }}>
                      <img
                        style={{ paddingTop: "10%", transform: "scale(0.6)" }}
                        alt=""
                        width="50%"
                        src="../../Assets/widget/order-delivery.png"
                      />
                    </div>
                  </div>
                </Card>
              </div>
              <div style={{ padding: "20px 0px 20px 20px" }} class="grid-item">
                <Card title="" sectioned>
                  <DisplayText size="medium">
                    {loading && <SkeletonBodyText lines={2} />}
                    {!loading && <>{Number(postOrderPaymentSentMessages)}</>}
                  </DisplayText>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <div>
                      <Br height={20} />
                      <TextStyle variation="subdued">Order</TextStyle>
                      <br />
                      <TextStyle variation="subdued">Confirmation</TextStyle>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <img
                        style={{ paddingTop: "10%", transform: "scale(0.5)" }}
                        alt=""
                        width="50%"
                        src="../../Assets/widget/order-confirmation.png"
                      />
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
        <Layout>
          <Layout.Section>
            <Card>
              <DataTable
                columnContentTypes={columnContentTypes()}
                headings={tableHeadings()}
                rows={loading ? skeleton : checkouts}
                footerContent={
                  loading
                    ? `Loading checkouts! please wait...`
                    : `Showing ${checkouts.length} of ${pageDetails.totalItems} results`
                }
              />
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    </>
  );
}

export default OrdersPage;
